import React, { useEffect, useMemo } from 'react'
import {
  useDeleteLocationConfiguration,
  useFindAllLocationConfigurationsForTenant,
} from 'src/api/queries/trade'
import Table2, { useTable } from 'src/ui/table2'
import {
  Box,
  Button,
  Center,
  IconButton,
  Spinner,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useCompanyContext } from 'src/companies/routes/company-context'
import { Dialog, Row } from 'src/ui'
import { Link, useNavigate } from 'react-router-dom'
import { getEnvironment } from 'src/utils'
import { hasPermission } from 'src/utils/permissions'
import LocationModal from './location-modal'
import { useToast } from 'src/utils/toast'
import { DeleteIcon } from '@chakra-ui/icons'

type DeleteLocationDialogState = {
  displayName: string
  locationId: string
}

function LocationActions({ row, onDelete }) {
  const { company } = useCompanyContext()

  return (
    <Row justifyContent="flex-end" alignItems="center">
      <Button
        size="sm"
        color="primary.500"
        variant="ghost"
        as={Link}
        isDisabled={!hasPermission('offer_management_edit')}
        to={[
          null,
          'companies',
          getEnvironment(),
          company.slug,
          'markets',
          'locations',
          row.original.locationId,
        ].join('/')}
      >
        Edit
      </Button>
      <Tooltip label="Delete Location Configuration" aria-label="Delete Location Configuration">
        <IconButton
          icon={<DeleteIcon />}
          aria-label="Delete"
          isRound
          variant="ghost"
          color="gray.600"
          isDisabled={!hasPermission('offer_management_edit')}
          onClick={() =>
            onDelete({
              displayName: row.original.displayName,
              locationId: row.original.locationId,
            })
          }
        />
      </Tooltip>
    </Row>
  )
}

export default function LocationIndex() {
  const { company } = useCompanyContext()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const navigate = useNavigate()
  const [deleteDialog, setDeleteDialog] = React.useState<DeleteLocationDialogState | null>(null)
  const [isDeleting, setIsDeleting] = React.useState(false)
  const { data, fetchNextPage, hasNextPage, isFetching, error } =
    useFindAllLocationConfigurationsForTenant()
  const { mutateAsync: deleteLocationConfiguration } = useDeleteLocationConfiguration()

  const handleAddClicked = (location: TenantLocation) =>
    navigate(
      [null, 'companies', getEnvironment(), company.slug, 'markets', 'locations', location.id].join(
        '/'
      )
    )

  const closeDeleteDialog = () => {
    if (isDeleting) return
    setDeleteDialog(null)
  }

  const handleDelete = async () => {
    if (!deleteDialog?.locationId) return

    setIsDeleting(true)
    try {
      await deleteLocationConfiguration({ id: deleteDialog.locationId })
      toast({ description: 'Successfully deleted location configuration' })
    } catch {
      toast({ status: 'error', description: 'Failed to delete location configuration' })
    } finally {
      setIsDeleting(false)
      setDeleteDialog(null)
    }
  }

  let columns = useMemo(
    () => [
      {
        id: 'displayName',
        accessor: 'displayName',
        Header: 'Name',
        disableSortBy: true,
      },
      {
        id: '_actions',
        accessor: '_actions',
        Header: '',
        disableSortBy: true,
        Cell: ({ row }) => <LocationActions row={row} onDelete={setDeleteDialog} />,
      },
    ],
    []
  )

  let { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable({
    data,
    tableOptions: {
      initialState: {
        sortBy: [],
      },
    },
    columns,
  })

  /**
   * This will load enough data to fill the page and make a scrollbar to appear. At that point,
   * react-infinite-scroll-component can handle loading additional data.
   */
  useEffect(() => {
    if (
      !isFetching &&
      !error &&
      hasNextPage &&
      document.getElementById('locations_scroll_container').scrollHeight <= window.innerHeight
    ) {
      fetchNextPage()
    }
  }, [isFetching, hasNextPage, fetchNextPage, error])

  return (
    <>
      <VStack>
        <Button
          alignSelf="end"
          size="sm"
          type="button"
          width="70px"
          colorScheme="primary"
          isDisabled={!hasPermission('offer_management_edit')}
          onClick={onOpen}
        >
          Add
        </Button>
        <Box
          id="locations_scroll_container"
          overflowY="auto"
          height="calc(100vh - 180px)"
          width="100%"
        >
          <Box bg="white" mb={4} pt={3} borderRadius={3} boxShadow="md">
            <InfiniteScroll
              next={fetchNextPage}
              hasMore={hasNextPage}
              loader={
                <Center h="50px">
                  <Spinner speed="0.8s" thickness="3px" color="inherit" />
                </Center>
              }
              dataLength={data?.length ?? 0}
              scrollableTarget="locations_scroll_container"
            >
              <Table2 {...getTableProps} striped>
                <Table2.Header headerGroups={headerGroups} />
                <Table2.Body
                  rows={rows}
                  getTableBodyProps={getTableBodyProps}
                  prepareRow={prepareRow}
                />
              </Table2>
            </InfiniteScroll>
          </Box>
        </Box>
      </VStack>
      <LocationModal isOpen={isOpen} onClose={onClose} handleAddClicked={handleAddClicked} />
      <Dialog
        title="Delete Location Configuration"
        isOpen={!!deleteDialog}
        onClose={closeDeleteDialog}
        actions={
          <>
            <Button size="sm" variant="ghost" mr={2} onClick={closeDeleteDialog}>
              Cancel
            </Button>
            <Button size="sm" colorScheme="primary" isLoading={isDeleting} onClick={handleDelete}>
              Delete
            </Button>
          </>
        }
      >
        <Box>
          <Box>Are you sure you want to delete this location configuration?</Box>
          {deleteDialog?.displayName && (
            <ul>
              <li>{deleteDialog.displayName}</li>
            </ul>
          )}
        </Box>
      </Dialog>
    </>
  )
}
